import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { LOGO_URL } from '@/constants/appMeta';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { Beaker, CheckCircle2 } from 'lucide-react';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

// Veřejná self-service registrace laboratoře (demo/trial). Po odeslání se
// laboratoř založí ve stavu „čeká na ověření e-mailu" — uživatel dostane
// odkaz na e-mail, po jeho potvrzení jde registrace do fronty ke schválení.
export default function RegisterLabPage() {
  const [form, setForm] = useState({
    labName: '', adminFirstName: '', adminLastName: '', email: '', password: '',
  });
  const [availableQuantities, setAvailableQuantities] = useState([]);
  const [selectedQuantities, setSelectedQuantities] = useState([]);
  const [captchaToken, setCaptchaToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null); // { verifyUrl? }
  const turnstileRef = useRef(null);

  useEffect(() => { document.title = 'Registrace laboratoře | CalibraTech 2.0'; }, []);

  // Veřejný číselník veličin (modulů) pro výběr při registraci (volitelný).
  useEffect(() => {
    api.get('/register-lab/quantities')
      .then((r) => setAvailableQuantities(r.data || []))
      .catch(() => setAvailableQuantities([]));
  }, []);

  const toggleQuantity = (code) =>
    setSelectedQuantities((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  // Cloudflare Turnstile — jen pokud je nastavený site key (jinak CAPTCHA vypnutá).
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return;
    const render = () => {
      if (window.turnstile && turnstileRef.current) {
        window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          callback: (tok) => setCaptchaToken(tok),
          'error-callback': () => setCaptchaToken(''),
        });
      }
    };
    if (window.turnstile) { render(); return; }
    const s = document.createElement('script');
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true; s.defer = true; s.onload = render;
    document.head.appendChild(s);
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 8) { setError('Heslo musí mít alespoň 8 znaků.'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/register-lab', { ...form, quantities: selectedQuantities, captchaToken });
      setDone({ verifyUrl: data.verifyUrl });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Registraci se nepodařilo dokončit. Zkuste to prosím znovu.');
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear();

  return (
    <div className="min-h-screen w-full flex" style={{ background: '#f8fafc' }}>
      {/* Levý brandový panel */}
      <aside
        className="relative hidden lg:flex flex-col justify-between overflow-hidden"
        style={{ width: '44%', maxWidth: '34rem', padding: '3rem', color: '#e2e8f0',
          background: 'linear-gradient(160deg, #0f2a6b 0%, #1d4ed8 55%, #2563eb 100%)' }}
      >
        <div className="relative z-10 flex items-center gap-3">
          <img src={LOGO_URL} alt="CalibraTech" className="w-10 h-10 rounded-lg object-contain p-1" style={{ background: '#ffffff' }} />
          <span className="text-xl font-bold tracking-tight text-white" style={{ fontFamily: 'Inter, sans-serif' }}>CalibraTech&nbsp;2.0</span>
        </div>
        <div className="relative z-10">
          <h2 className="text-white font-bold m-0 mb-4" style={{ fontFamily: 'Inter, sans-serif', fontSize: '2rem', lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            Vyzkoušejte CalibraTech pro svou laboratoř
          </h2>
          <p className="m-0 mb-8" style={{ fontSize: '1rem', lineHeight: 1.6, color: 'rgba(226, 232, 240, 0.85)', maxWidth: '24rem' }}>
            Založte si vlastní demo prostředí s ukázkovými daty — přístroje, kalibrace, kalibrační listy. Žádná instalace, stačí e-mail.
          </p>
        </div>
        <p className="relative z-10 m-0" style={{ fontSize: '0.75rem', color: 'rgba(226, 232, 240, 0.7)' }}>CalibraTech 2.0 &copy; {year}</p>
      </aside>

      {/* Pravá strana — formulář / potvrzení */}
      <main className="flex-1 flex items-center justify-center" style={{ padding: '2rem 1.5rem' }}>
        <div className="w-full" style={{ maxWidth: '28rem' }}>
          <div className="flex lg:hidden items-center justify-center gap-2.5 mb-8">
            <img src={LOGO_URL} alt="CalibraTech" className="w-9 h-9 rounded-lg object-contain" />
            <span className="text-lg font-bold" style={{ fontFamily: 'Inter, sans-serif', color: '#2563eb' }}>CalibraTech 2.0</span>
          </div>

          <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '0.75rem', padding: '2rem',
            boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.08), 0 4px 10px -6px rgba(15, 23, 42, 0.05)' }}>

            {done ? (
              <div className="text-center">
                <div className="flex justify-center mb-4"><CheckCircle2 className="w-12 h-12" style={{ color: '#16a34a' }} /></div>
                <h1 className="m-0 font-bold" style={{ fontSize: '1.4rem', color: '#0f172a' }}>Registrace odeslána</h1>
                <p className="m-0 mt-3" style={{ fontSize: '0.9rem', color: '#475569', lineHeight: 1.6 }}>
                  Děkujeme! Jakmile registraci schválíme, připravíme vám demo prostředí a dáme vědět na zadaný e-mail.
                </p>
                <Link to="/login" className="inline-block mt-6 text-sm font-medium" style={{ color: '#2563eb' }}>Zpět na přihlášení</Link>
              </div>
            ) : (
              <>
                <div className="mb-6 flex items-center gap-2">
                  <Beaker className="w-6 h-6" style={{ color: '#2563eb' }} />
                  <h1 className="m-0 font-bold" style={{ fontFamily: 'Inter, sans-serif', fontSize: '1.5rem', letterSpacing: '-0.02em', color: '#0f172a' }}>
                    Registrace laboratoře
                  </h1>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Field label="Název laboratoře" value={form.labName} onChange={set('labName')} placeholder="Např. Kalibrační laboratoř ACME" autoFocus />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Jméno" value={form.adminFirstName} onChange={set('adminFirstName')} placeholder="Jan" />
                    <Field label="Příjmení" value={form.adminLastName} onChange={set('adminLastName')} placeholder="Novák" />
                  </div>
                  <Field label="E-mail" type="email" value={form.email} onChange={set('email')} placeholder="jan.novak@acme.cz" />
                  <Field label="Heslo" type="password" value={form.password} onChange={set('password')} placeholder="Alespoň 8 znaků" />

                  {availableQuantities.length > 0 && (
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium uppercase tracking-wider" style={{ color: '#475569' }}>
                        Jaká měření chcete provádět? <span style={{ textTransform: 'none', color: '#94a3b8' }}>(volitelné)</span>
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        {availableQuantities.map((q) => {
                          const active = selectedQuantities.includes(q.code);
                          return (
                            <button
                              type="button"
                              key={q.code}
                              onClick={() => toggleQuantity(q.code)}
                              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors"
                              style={{
                                border: `1px solid ${active ? '#2563eb' : '#e2e8f0'}`,
                                background: active ? '#eff6ff' : '#fff',
                                color: active ? '#1d4ed8' : '#475569',
                                fontWeight: active ? 600 : 400,
                              }}
                            >
                              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: q.color || '#94a3b8' }} />
                              {q.name}
                            </button>
                          );
                        })}
                      </div>
                      <p style={{ fontSize: '0.72rem', color: '#94a3b8', margin: 0 }}>
                        O další moduly můžete požádat kdykoli později v nastavení laboratoře.
                      </p>
                    </div>
                  )}

                  {TURNSTILE_SITE_KEY && <div ref={turnstileRef} className="pt-1" />}

                  {error && (
                    <div className="text-sm p-2 rounded" style={{ background: 'var(--ct-error-soft)', color: 'var(--ct-error)', border: '1px solid var(--ct-error)' }}>
                      {error}
                    </div>
                  )}

                  <Button type="submit" disabled={loading} className="w-full font-semibold"
                    style={{ background: '#2563eb', color: '#fff', height: '2.75rem', borderRadius: '0.5rem' }}>
                    {loading ? 'Odesílám…' : 'Vytvořit demo laboratoř'}
                  </Button>
                </form>

                <p className="text-center mt-5" style={{ fontSize: '0.8rem', color: '#64748b' }}>
                  Už máte účet? <Link to="/login" className="font-medium" style={{ color: '#2563eb' }}>Přihlaste se</Link>
                </p>
              </>
            )}
          </div>
          <p className="text-center mt-8" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />Demo prostředí je izolované a časově omezené.
          </p>
        </div>
      </main>
    </div>
  );
}

function Field({ label, type = 'text', value, onChange, placeholder, autoFocus }) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium uppercase tracking-wider" style={{ color: '#475569' }}>{label}</label>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} autoFocus={autoFocus} className="h-10" />
    </div>
  );
}
